/**
 * The one place the browser talks to the API.
 *
 * A copy of the staff application's `src/web/src/lib/api.ts` with three exports removed
 * rather than rewritten: the blob download, the multipart upload and the anonymous
 * client. The portal downloads nothing, uploads nothing, and has no anonymous route -
 * every portal endpoint is authenticated, and the two anonymous groups that exist
 * (customer acceptance and verification) belong to the other application.
 *
 * Everything goes through `request`, so the bearer token, the renewal dance and
 * RFC 7807 error shapes are handled once rather than in forty components.
 *
 * The tokens themselves belong to `session.ts`, which holds them in memory and gets
 * them from ARMS. Nothing here reads or writes storage.
 */

import { getAccessToken, refreshSession } from './session'
import { BASE } from './base'

/** An RFC 7807 problem, or whatever the server managed to say instead. */
export class ApiError extends Error {
  readonly status: number
  readonly title: string
  readonly detail: string | undefined
  readonly blockers: readonly Blocker[]

  constructor(status: number, title: string, detail?: string, blockers: Blocker[] = []) {
    super(detail && detail.length > 0 ? detail : title)
    this.name = 'ApiError'
    this.status = status
    this.title = title
    this.detail = detail
    this.blockers = blockers
  }

  get isAuth(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }
}

export interface Blocker {
  readonly code: string
  readonly message: string
}

interface ProblemDetails {
  title?: string
  detail?: string
  status?: number
  blockers?: Blocker[]
}

export interface RequestOptions {
  method?: string
  body?: unknown
  query?: Record<string, string | number | boolean | null | undefined>
  signal?: AbortSignal
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options)

  // One retry, and only after a refresh that actually succeeded. Retrying a 401
  // without new credentials is how a client ends up hammering a dead session.
  if (response.status === 401 && (await refreshSession())) {
    const retried = await send(path, options)
    return unwrap<T>(retried)
  }

  return unwrap<T>(response)
}

function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' }

  const token = getAccessToken()

  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`

  return fetch(BASE + path + queryString(options.query), {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })
}

async function unwrap<T>(response: Response): Promise<T> {
  if (!response.ok) {
    throw await problemFrom(response)
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }

  return (await response.json()) as T
}

async function problemFrom(response: Response): Promise<ApiError> {
  let problem: ProblemDetails = {}

  try {
    problem = (await response.json()) as ProblemDetails
  } catch {
    // A gateway timeout or a proxy error page. Nothing to parse.
  }

  return new ApiError(
    response.status,
    problem.title ?? defaultTitle(response.status),
    problem.detail,
    problem.blockers ?? [],
  )
}

function defaultTitle(status: number): string {
  if (status === 401) return 'Your session has ended'
  if (status === 403) return 'You do not have permission to do that'
  if (status === 404) return 'Not found'
  if (status >= 500) return 'The server could not complete that'
  return 'That did not work'
}

export function queryString(
  query: Record<string, string | number | boolean | null | undefined> | undefined,
): string {
  if (!query) return ''

  const parts = new URLSearchParams()

  for (const [key, value] of Object.entries(query)) {
    // Deliberately drops null, undefined and empty strings rather than sending
    // them: an empty filter means "no filter", not "match the empty string".
    if (value === null || value === undefined || value === '') continue
    parts.append(key, String(value))
  }

  const rendered = parts.toString()
  return rendered ? `?${rendered}` : ''
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
